/**
 * Cheap 1D-barcode localization. Bars have a unique signature: dense
 * HORIZONTAL gradients with almost no VERTICAL gradients. Scoring h−v per
 * grid cell finds bar regions even when the engine's own scanline
 * localization drowns in a busy frame (label text, background clutter) —
 * the exact failure mode captured from a real lab-label debug frame.
 */

export interface NormalizedRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

const CELL = 16;
/** Minimum average per-pixel gradient excess for a region's peak cell. */
const MIN_CELL_SCORE = CELL * CELL * 8;

/** Greedily extract up to `max` distinct bar regions, strongest first. */
export function locateBarcodes(img: ImageData, max = 3): NormalizedRect[] {
  const { width: w, height: h, data } = img;
  const gw = Math.floor(w / CELL);
  const gh = Math.floor(h / CELL);
  if (gw < 2 || gh < 2) return [];

  const score = new Float32Array(gw * gh);
  for (let gy = 0; gy < gh; gy++) {
    for (let gx = 0; gx < gw; gx++) {
      let hSum = 0;
      let vSum = 0;
      const x0 = gx * CELL;
      const y0 = gy * CELL;
      for (let y = y0; y < y0 + CELL - 1; y++) {
        const row = y * w;
        const rowNext = row + w;
        for (let x = x0; x < x0 + CELL - 1; x++) {
          // green channel ≈ luma, avoids a full grayscale pass
          const g = data[(row + x) * 4 + 1];
          hSum += Math.abs(data[(row + x + 1) * 4 + 1] - g);
          vSum += Math.abs(data[(rowNext + x) * 4 + 1] - g);
        }
      }
      score[gy * gw + gx] = hSum - vSum;
    }
  }

  const rects: NormalizedRect[] = [];
  for (let n = 0; n < max; n++) {
    let best = 0;
    let bestIdx = -1;
    for (let i = 0; i < score.length; i++) {
      if (score[i] > best) {
        best = score[i];
        bestIdx = i;
      }
    }
    if (bestIdx < 0 || best < MIN_CELL_SCORE) break;

    const by = Math.floor(bestIdx / gw);
    let minX = bestIdx % gw;
    let maxX = minX;
    let minY = by;
    let maxY = by;
    // Grow over strong cells near the best row (bars span horizontally; the
    // row limit keeps label text lines from stretching the crop vertically).
    for (let gy = Math.max(0, by - 5); gy < Math.min(gh, by + 6); gy++) {
      for (let gx = 0; gx < gw; gx++) {
        if (score[gy * gw + gx] >= best * 0.3) {
          if (gx < minX) minX = gx;
          if (gx > maxX) maxX = gx;
          if (gy < minY) minY = gy;
          if (gy > maxY) maxY = gy;
        }
      }
    }

    // Consume this region so the next iteration finds the next barcode.
    for (let gy = Math.max(0, minY - 1); gy <= Math.min(gh - 1, maxY + 1); gy++) {
      for (let gx = Math.max(0, minX - 1); gx <= Math.min(gw - 1, maxX + 1); gx++) {
        score[gy * gw + gx] = 0;
      }
    }

    // one-cell quiet-zone padding, clamped
    const x = Math.max(0, (minX - 1) * CELL) / w;
    const y = Math.max(0, (minY - 1) * CELL) / h;
    const x2 = Math.min(w, (maxX + 2) * CELL) / w;
    const y2 = Math.min(h, (maxY + 2) * CELL) / h;
    rects.push({ x, y, width: x2 - x, height: y2 - y });
  }
  return rects;
}

/** Strongest single region, or null. */
export function locateBarcode(img: ImageData): NormalizedRect | null {
  return locateBarcodes(img, 1)[0] ?? null;
}
